import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Shield, Lock, Zap, EyeOff, Terminal, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Landing() {
  const navigate = useNavigate();
  const [joinRoomId, setJoinRoomId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  const createRoom = async () => {
    setIsCreating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (!response.ok) throw new Error('Failed to create room');
      
      const data = await response.json();
      navigate(`/room/${data.room_id}`);
    } catch (error) {
      toast.error('Failed to create room. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const joinRoom = async (e) => {
    e.preventDefault();
    
    // Extract room ID from full URL or use as-is
    let roomId = joinRoomId.trim();
    const urlMatch = roomId.match(/\/room\/([a-f0-9-]+)/i);
    if (urlMatch) {
      roomId = urlMatch[1];
    }
    
    // Validate UUID format
    const uuidRegex = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
    if (!uuidRegex.test(roomId)) {
      toast.error('Invalid room ID format');
      return;
    }
    
    setIsJoining(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/rooms/${roomId}/status`);
      const status = await response.json();
      
      if (status.is_full) {
        toast.error('Room is full (max 2 users)');
        return;
      }
      
      navigate(`/room/${roomId}`);
    } catch (error) {
      toast.error('Failed to join room');
    } finally {
      setIsJoining(false);
    }
  };

  const features = [
    { icon: EyeOff, title: 'NO STORAGE', desc: 'Messages exist only in transit' },
    { icon: Lock, title: 'E2E ENCRYPTED', desc: 'Keys never leave your device' },
    { icon: Trash2, title: 'SELF-DESTRUCT', desc: 'Room dies when empty' },
    { icon: Zap, title: 'EPHEMERAL', desc: 'Refresh = gone forever' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Noise overlay */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.03] z-50"
           style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />
      
      {/* Header */}
      <header className="border-b border-white/10 p-4">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <Terminal className="w-6 h-6 text-green-500" />
          <span className="font-mono font-bold tracking-tight text-lg">GHOSTTALK</span>
        </div>
      </header>

      {/* Main content - Mobile optimized */}
      <main className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8">
        <div className="max-w-4xl w-full space-y-8 sm:space-y-16">
          
          {/* Hero - Mobile optimized */}
          <div className="text-center space-y-4 sm:space-y-6">
            <h1 className="font-mono font-black text-3xl sm:text-5xl md:text-7xl tracking-tighter">
              EPHEMERAL<br />
              <span className="text-green-500">MESSAGING</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 max-w-xl mx-auto font-light px-2">
              Privacy-first. No accounts. No history. No trace.
              <br className="hidden sm:block" />
              <span className="sm:hidden"> </span>Messages vanish the moment you disconnect.
            </p>
          </div>

          {/* Action cards - Mobile optimized */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            {/* Create Room */}
            <Card className="bg-[#0A0A0A] border-2 border-white/10 rounded-none p-5 sm:p-8 hover:border-green-500/50 transition-colors duration-200">
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 sm:w-6 h-5 sm:h-6 text-green-500 flex-shrink-0" />
                  <h2 className="font-mono font-bold text-lg sm:text-xl tracking-tight">NEW ROOM</h2>
                </div>
                <p className="text-zinc-400 text-sm">
                  Generate a secure room with a unique invite link.
                  Share it with one person only.
                </p>
                <Button
                  data-testid="create-room-btn"
                  onClick={createRoom}
                  disabled={isCreating}
                  className="w-full bg-green-500 text-black font-mono font-bold rounded-sm hover:bg-white hover:text-black transition-colors duration-150 min-h-[48px]"
                >
                  {isCreating ? 'CREATING...' : 'CREATE ROOM →'}
                </Button>
              </div>
            </Card>

            {/* Join Room */}
            <Card className="bg-[#0A0A0A] border-2 border-white/10 rounded-none p-5 sm:p-8 hover:border-green-500/50 transition-colors duration-200">
              <form onSubmit={joinRoom} className="space-y-4 sm:space-y-6">
                <div className="flex items-center gap-3">
                  <Lock className="w-5 sm:w-6 h-5 sm:h-6 text-green-500 flex-shrink-0" />
                  <h2 className="font-mono font-bold text-lg sm:text-xl tracking-tight">JOIN ROOM</h2>
                </div>
                <p className="text-zinc-400 text-sm">
                  Enter a room ID or paste the full invite link.
                </p>
                <Input
                  data-testid="join-room-input"
                  type="text"
                  placeholder="Room ID or invite link..."
                  value={joinRoomId}
                  onChange={(e) => setJoinRoomId(e.target.value)}
                  className="bg-transparent border-0 border-b-2 border-white/20 rounded-none font-mono text-sm placeholder:text-zinc-600 focus:border-green-500 focus:ring-0 min-h-[44px]"
                />
                <Button
                  data-testid="join-room-btn"
                  type="submit"
                  disabled={isJoining || !joinRoomId.trim()}
                  className="w-full bg-white/10 text-white font-mono font-bold rounded-sm hover:bg-white hover:text-black transition-colors duration-150 disabled:opacity-50 min-h-[48px]"
                >
                  {isJoining ? 'JOINING...' : 'JOIN →'}
                </Button>
              </form>
            </Card>
          </div>

          {/* Features grid - Mobile optimized */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {features.map((feature) => (
              <div key={feature.title} className="border border-white/10 p-3 sm:p-4 hover:border-green-500/30 transition-colors duration-200">
                <feature.icon className="w-4 sm:w-5 h-4 sm:h-5 text-green-500 mb-2 sm:mb-3" />
                <h3 className="font-mono font-bold text-[10px] sm:text-xs tracking-wide mb-1">{feature.title}</h3>
                <p className="text-zinc-500 text-[10px] sm:text-xs leading-tight">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Warning - Mobile optimized */}
          <div className="text-center text-zinc-600 text-[10px] sm:text-xs font-mono space-y-1 sm:space-y-2 px-2">
            <p>⚠ NO RECOVERY • NO BACKUPS • NO HISTORY</p>
            <p className="text-zinc-700">refresh = gone • disconnect = destroyed</p>
          </div>
        </div>
      </main>

      {/* Footer - Mobile optimized with safe area */}
      <footer className="border-t border-white/10 p-3 sm:p-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">
        <div className="max-w-6xl mx-auto text-center text-zinc-600 text-[10px] sm:text-xs font-mono">
          <p>PRIVACY &gt; CONVENIENCE • NO LOGS • NO TRACKING</p>
        </div>
      </footer>
    </div>
  );
}
